import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { CompanyUploadPreview, JobTitleScreeningPreview } from "@signalist/shared-types";

import { Accordion } from "@/components/Accordion/Accordion";
import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";

import { RowLevelStats } from "./titleGate.utils";
import { buildXlsStatisticsRows } from "./xlsStatisticsRows";

interface XlsStatisticsProps {
	rowStats: RowLevelStats;
	entityType?: string | null;
	preview?: JobTitleScreeningPreview;
	companyPreview?: CompanyUploadPreview;
}

/**
 * PREC-3536 Phase-4 collapsible XLS Statistics box. Row-level figures (rows detected / ready /
 * duplicates) are computed client-side from the SheetJS parse; the trailing pair comes from the
 * list type's dry-run — job-title verdicts for a people upload, known-vs-new companies for a
 * company upload (PREC-3870). Which rows there are is decided in `xlsStatisticsRows.ts`.
 */
export const XlsStatistics: FunctionComponent<XlsStatisticsProps> = ({
	rowStats,
	entityType,
	preview,
	companyPreview,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`csvEnrichment.titleGate.${key}`);

	const rows = buildXlsStatisticsRows({ entityType, rowStats, preview, companyPreview });

	return (
		<div
			className="mt-4 rounded-xl border border-gray-200 bg-white px-4"
			data-test="xls-statistics"
		>
			<Accordion
				items={[
					{
						id: "xls-statistics",
						title: ts("statsTitle"),
						content: (
							<div className="flex flex-col gap-1">
								{rows.map((row) => (
									<div
										key={row.key}
										className="flex flex-row items-center gap-3 py-1.5 text-sm"
										data-test={`xls-statistics-${row.key}`}
									>
										<span
											className={classNames(
												"flex h-7 w-7 items-center justify-center rounded-lg",
												row.iconClass
											)}
										>
											<Icon className="h-3.5 w-3.5" icon={row.icon} />
										</span>
										<span className="flex-1 text-text-meta">{ts(row.key)}</span>
										<span className="font-semibold text-text">{row.value}</span>
									</div>
								))}
							</div>
						),
					},
				]}
			/>
		</div>
	);
};
